"use client";

import * as React from "react";

export type SatelliteApplication = "agentdirectory" | "orgportal";
export type SatelliteNavigationOutcome = "completed" | "failed" | "abandoned";

export interface SatellitePageLoadEvent {
  application: SatelliteApplication;
  tenant_key: string;
  route_template: string;
  navigation_type: string;
  duration_ms: number;
  loading_ui_appeared: boolean;
  terminal_outcome: SatelliteNavigationOutcome;
  release: string;
}

interface BeginNavigationOptions {
  routeTemplate: string;
  navigationType: string;
  targetKey: string;
  targetHref?: string;
  control?: HTMLElement | null;
}

interface PendingNavigation extends BeginNavigationOptions {
  startedAt: number;
  loadingUiAppeared: boolean;
  previousAriaBusy: string | null;
  previousAriaDisabled: string | null;
}

interface FeedbackState {
  pending: boolean;
  loadingUiAppeared: boolean;
  slow: boolean;
}

interface SatelliteNavigationContextValue {
  beginNavigation: (options: BeginNavigationOptions) => boolean;
  finishNavigation: (
    targetKey: string,
    outcome: SatelliteNavigationOutcome,
  ) => void;
  isPending: boolean;
}

const SatelliteNavigationContext = React.createContext<
  SatelliteNavigationContextValue | undefined
>(undefined);

let activeFailureReporter: (() => void) | null = null;

/** Mark the active tracked navigation as failed from a Next error boundary. */
export function markSatelliteNavigationFailed(): void {
  activeFailureReporter?.();
}

export function useSatelliteNavigation(): SatelliteNavigationContextValue {
  const value = React.useContext(SatelliteNavigationContext);
  if (!value) {
    throw new Error(
      "useSatelliteNavigation must be used within SatelliteNavigationProvider",
    );
  }
  return value;
}

/** Shared packages may render outside tracked satellite apps. */
export function useSatelliteNavigationOptional():
  | SatelliteNavigationContextValue
  | undefined {
  return React.useContext(SatelliteNavigationContext);
}

interface SatelliteNavigationProviderProps {
  application: SatelliteApplication;
  tenantKey: string;
  release: string;
  currentNavigationKey: string;
  routeTemplateForPath: (pathname: string) => string;
  onOutcome: (event: SatellitePageLoadEvent) => void;
  children?: React.ReactNode;
}

const INITIAL_FEEDBACK: FeedbackState = {
  pending: false,
  loadingUiAppeared: false,
  slow: false,
};

/**
 * Shared tenant-agnostic navigation contract for satellite applications.
 *
 * Internal links are observed in the capture phase, before Next handles them.
 * Programmatic data actions call the exposed hook immediately before router.push.
 */
export function SatelliteNavigationProvider({
  application,
  tenantKey,
  release,
  currentNavigationKey,
  routeTemplateForPath,
  onOutcome,
  children,
}: SatelliteNavigationProviderProps) {
  const pendingRef = React.useRef<PendingNavigation | null>(null);
  const delayedTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const slowTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialNavigationKeyRef = React.useRef(currentNavigationKey);
  const [feedback, setFeedback] = React.useState(INITIAL_FEEDBACK);

  const clearTimers = React.useCallback(() => {
    if (delayedTimerRef.current) clearTimeout(delayedTimerRef.current);
    if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
    delayedTimerRef.current = null;
    slowTimerRef.current = null;
  }, []);

  const restoreControl = React.useCallback((pending: PendingNavigation) => {
    const control = pending.control;
    if (!control) return;

    control.removeAttribute("data-satellite-pending");
    if (pending.previousAriaBusy === null) control.removeAttribute("aria-busy");
    else control.setAttribute("aria-busy", pending.previousAriaBusy);
    if (pending.previousAriaDisabled === null)
      control.removeAttribute("aria-disabled");
    else control.setAttribute("aria-disabled", pending.previousAriaDisabled);
  }, []);

  const finalize = React.useCallback(
    (outcome: SatelliteNavigationOutcome) => {
      const pending = pendingRef.current;
      if (!pending) return;

      pendingRef.current = null;
      clearTimers();
      restoreControl(pending);
      document.documentElement.removeAttribute("data-satellite-navigation-pending");
      document.documentElement.removeAttribute("aria-busy");
      setFeedback(INITIAL_FEEDBACK);

      const event: SatellitePageLoadEvent = {
        application,
        tenant_key: tenantKey,
        route_template: pending.routeTemplate,
        navigation_type: pending.navigationType,
        duration_ms: Math.max(0, Date.now() - pending.startedAt),
        loading_ui_appeared: pending.loadingUiAppeared,
        terminal_outcome: outcome,
        release: release || "unknown",
      };

      try {
        onOutcome(event);
      } catch {
        // Telemetry must never affect navigation.
      }
    }, [application, clearTimers, onOutcome, release, restoreControl, tenantKey],
  );

  const beginNavigation = React.useCallback(
    (options: BeginNavigationOptions): boolean => {
      const active = pendingRef.current;
      if (active?.targetKey === options.targetKey) return false;
      if (active) finalize("abandoned");

      const control = options.control ?? null;
      const pending: PendingNavigation = {
        ...options,
        control,
        startedAt: Date.now(),
        loadingUiAppeared: false,
        previousAriaBusy: control?.getAttribute("aria-busy") ?? null,
        previousAriaDisabled: control?.getAttribute("aria-disabled") ?? null,
      };
      pendingRef.current = pending;

      control?.setAttribute("data-satellite-pending", "true");
      control?.setAttribute("aria-busy", "true");
      control?.setAttribute("aria-disabled", "true");
      document.documentElement.setAttribute(
        "data-satellite-navigation-pending",
        "true",
      );
      document.documentElement.setAttribute("aria-busy", "true");
      setFeedback({ pending: true, loadingUiAppeared: false, slow: false });

      delayedTimerRef.current = setTimeout(() => {
        const current = pendingRef.current;
        if (!current) return;
        current.loadingUiAppeared = true;
        setFeedback({ pending: true, loadingUiAppeared: true, slow: false });
      }, 200);

      slowTimerRef.current = setTimeout(() => {
        const current = pendingRef.current;
        if (!current) return;
        current.loadingUiAppeared = true;
        setFeedback({ pending: true, loadingUiAppeared: true, slow: true });
      }, 10_000);
      return true;
    },
    [finalize],
  );

  const finishNavigation = React.useCallback(
    (targetKey: string, outcome: SatelliteNavigationOutcome) => {
      if (pendingRef.current?.targetKey !== targetKey) return;
      finalize(outcome);
    },
    [finalize],
  );

  React.useEffect(() => {
    if (initialNavigationKeyRef.current === currentNavigationKey) return;
    initialNavigationKeyRef.current = currentNavigationKey;
    finalize("completed");
  }, [currentNavigationKey, finalize]);

  React.useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest("a[href]");
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (anchor.target && anchor.target !== "_self") return;
      if (anchor.hasAttribute("download")) return;

      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      const targetKey = `${url.pathname}${url.search}`;
      const currentKey = `${window.location.pathname}${window.location.search}`;
      if (targetKey === currentKey) return;

      const started = beginNavigation({
        routeTemplate: routeTemplateForPath(url.pathname),
        navigationType: "link",
        targetKey,
        targetHref: `${url.pathname}${url.search}${url.hash}`,
        control: anchor,
      });
      if (!started) {
        event.preventDefault();
        event.stopPropagation();
      }
    }

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, [beginNavigation, routeTemplateForPath]);

  React.useEffect(() => {
    function noteLoadingUi() {
      const pending = pendingRef.current;
      if (!pending) return;
      pending.loadingUiAppeared = true;
    }

    function abandon() {
      finalize("abandoned");
    }

    window.addEventListener("satellite-loading-ui", noteLoadingUi);
    window.addEventListener("pagehide", abandon);
    return () => {
      window.removeEventListener("satellite-loading-ui", noteLoadingUi);
      window.removeEventListener("pagehide", abandon);
    };
  }, [finalize]);

  React.useEffect(() => {
    activeFailureReporter = () => finalize("failed");
    return () => {
      if (activeFailureReporter) activeFailureReporter = null;
      // A client-side route-group transition unmounts the source layout before
      // the destination provider can observe the new key. Reaching that
      // unmount is the completion signal. Full document exits fire `pagehide`
      // first and remain correctly classified as abandoned.
      finalize("completed");
    };
  }, [finalize]);

  const contextValue = React.useMemo(
    () => ({ beginNavigation, finishNavigation, isPending: feedback.pending }),
    [beginNavigation, feedback.pending, finishNavigation],
  );

  const retry = React.useCallback(() => {
    const targetHref = pendingRef.current?.targetHref;
    if (targetHref) window.location.assign(targetHref);
    else window.location.reload();
  }, []);

  return (
    <SatelliteNavigationContext.Provider value={contextValue}>
      {children}
      {feedback.pending ? (
        <div data-satellite-navigation-feedback>
          <div
            aria-hidden="true"
            className="fixed inset-x-0 top-0 z-[1000] h-1 animate-pulse bg-[var(--brand-primary)] motion-reduce:animate-none"
          />
          {feedback.loadingUiAppeared ? (
            <div
              className="fixed right-4 top-4 z-[1001] max-w-sm rounded-lg border bg-[var(--brand-card-bg)] px-4 py-3 shadow-lg"
              style={{
                borderColor: "var(--brand-card-border)",
                color: "var(--brand-text)",
              }}
              role="status"
              aria-live="polite"
            >
              <p className="text-sm font-medium">
                {feedback.slow
                  ? "This is taking longer than expected."
                  : "Loading…"}
              </p>
              {feedback.slow ? (
                <button
                  type="button"
                  onClick={retry}
                  className="mt-2 min-h-11 rounded-md border px-3 py-2 text-sm font-medium"
                  style={{ borderColor: "var(--brand-card-border)" }}
                >
                  Retry
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </SatelliteNavigationContext.Provider>
  );
}
